import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

export interface VoiceUploadResult {
  url: string;
  fileName: string;
  size: number;
  duration?: number;
}

export class VoiceUploadService {
  private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  private static readonly MAX_DURATION = 300; // 5 minutes in seconds
  private static readonly ALLOWED_TYPES = [
    'audio/webm',
    'audio/ogg',
    'audio/mpeg',
    'audio/mp4',
    'audio/wav'
  ];

  static async uploadChatAudio(blob: Blob, userId: string, groupId: string, duration?: number): Promise<VoiceUploadResult> {
    this.validateFile(blob, duration);

    const timestamp = Date.now();
    const extension = this.getFileExtension(blob.type);
    const fileName = `chat-audio/${groupId}/${userId}/${timestamp}.${extension}`;
    
    // Log compression info for monitoring
    const compressionRatio = duration ? (blob.size / duration / 1024).toFixed(2) : 'unknown';
    console.log(`📼 Voice Upload: ${blob.type}, ${(blob.size / 1024).toFixed(1)}KB, ${duration}s, ${compressionRatio}KB/s`);
    
    try {
      const audioRef = ref(storage, fileName);
      const metadata = {
        contentType: blob.type,
        customMetadata: {
          uploadedBy: userId,
          groupId: groupId,
          duration: duration?.toString() || '0',
          originalSize: blob.size.toString(),
          compressionRatio: compressionRatio,
          uploadTimestamp: timestamp.toString()
        }
      };

      const snapshot = await uploadBytes(audioRef, blob, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        url: downloadURL,
        fileName: fileName,
        size: blob.size,
        duration: duration
      };
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw new Error('Failed to upload audio. Please try again.');
    }
  }

  static async deleteAudio(fileName: string): Promise<void> {
    try {
      const audioRef = ref(storage, fileName);
      await deleteObject(audioRef);
    } catch (error) {
      console.error('Error deleting audio:', error);
      // Don't throw error for deletion failures
    }
  }

  private static validateFile(blob: Blob, duration?: number): void {
    if (blob.size > this.MAX_FILE_SIZE) {
      throw new Error(`Audio file is too large. Maximum size is ${this.MAX_FILE_SIZE / (1024 * 1024)}MB.`);
    }

    if (duration && duration > this.MAX_DURATION) {
      throw new Error(`Audio is too long. Maximum duration is ${this.MAX_DURATION / 60} minutes.`);
    }

    // Extract base MIME type (remove codec specifications like ;codecs=opus)
    const baseMimeType = blob.type.split(';')[0];
    if (!this.ALLOWED_TYPES.includes(baseMimeType)) {
      throw new Error('Unsupported audio format. Please try again.');
    }
  }

  private static getFileExtension(mimeType: string): string {
    // Extract base MIME type (remove codec specifications)
    const baseMimeType = mimeType.split(';')[0];
    const extensions: { [key: string]: string } = {
      'audio/webm': 'webm',
      'audio/ogg': 'ogg',
      'audio/mpeg': 'mp3',
      'audio/mp4': 'm4a',
      'audio/wav': 'wav'
    };
    return extensions[baseMimeType] || 'webm';
  }

  static formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  static isRecordingSupported(): boolean {
    return 'MediaRecorder' in window && 'getUserMedia' in navigator.mediaDevices;
  }
}

export default VoiceUploadService; 